import type { ConnectedPosition, OverlayRef } from '@angular/cdk/overlay';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ComponentPortal } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import type { ComponentRef, OnDestroy } from '@angular/core';
import { Injectable, ViewContainerRef, inject } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';
import { Subject, tap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { UnwrapInput } from '@core/common-legacy';
import type {
  SymbolVirtualScrollRow,
  EmojiListNavigationKeys,
  EmojiItem,
  SymbolDataSource,
  SymbolSelectItem,
  SymbolSelectEmoji,
  SymbolGroup,
} from '@core/symbol-data';
import {
  EMOJI_GROUPED,
  SymbolDataSourceId,
  ICONS_GROUPED,
  EmojiCategoryTranslations,
} from '@core/symbol-data';

import { EmojiRecentlyUsedService } from './emoji-recently-used.service';
import { datasourceFilterFn, SymbolPickerComponent } from './symbol-picker/symbol-picker.component';
import { injectSymbolPickerTranslations } from './symbol-translations.service';

interface OverlayOrigin {
  top: number;
  left: number;
  right?: number;
  bottom?: number;
}

interface EmojiPickerOpenOptions {
  withSearchInput?: boolean;
  searchValue?: string;
  preferredPositions?: ConnectedPosition[];
  hasClearButton?: boolean;
  dataSources: Array<`${SymbolDataSourceId}`>;
}

@Injectable()
export class SymbolPickerService implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly _viewportRuler = inject(ViewportRuler);
  private readonly _document = inject<Document>(DOCUMENT);
  private readonly _platform = inject(Platform);
  private readonly _overlayContainer = inject(OverlayContainer);
  private readonly emojiRecentlyUsedService = inject(EmojiRecentlyUsedService);

  private readonly translations = injectSymbolPickerTranslations();
  public readonly SYMBOL_DATA_SOURCES = [
    <SymbolDataSource<EmojiItem>>{
      id: SymbolDataSourceId.Emoji,
      label: this.translations.tabEmojis,
      groups: EMOJI_GROUPED,
    },
    <SymbolDataSource<string>>{
      id: SymbolDataSourceId.MaterialIcons,
      label: this.translations.tabIcons,
      groups: ICONS_GROUPED,
    },
  ];

  private componentRef: ComponentRef<SymbolPickerComponent> | null = null;
  private overlayRef: OverlayRef | null = null;
  private positionStrategy: FlexibleConnectedPositionStrategy | null = null;
  private ngOnDestroy$ = new Subject<void>();
  private positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  private portalRef: ComponentPortal<SymbolPickerComponent> | null;

  public ngOnDestroy(): void {
    this.close();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public open<T extends EmojiPickerOpenOptions>(
    origin: OverlayOrigin,
    _options: T = <T>{ dataSources: ['emojis'] },
  ): {
    select$: Observable<Extract<SymbolSelectItem, { dataSourceId: T['dataSources'][number] }>>;
    clear$: Observable<void>;
    element: HTMLElement;
    backdropClick$: Observable<MouseEvent>;
  } {
    this.close();
    const options = this.getOpenOptions(_options);
    const componentRef = this.openComponent(
      SymbolPickerComponent,
      origin,
      options.preferredPositions,
    );
    const { instance, location, changeDetectorRef } = componentRef;
    const dataSources = this.getDataSources(options.dataSources);

    componentRef.setInput('dataSources', dataSources);
    componentRef.setInput('withSearchInput', options.withSearchInput);
    componentRef.setInput('hasClearButton', options.hasClearButton);
    if (options.searchValue) {
      componentRef.setInput('searchValue', options.searchValue);
    }

    changeDetectorRef.detectChanges();
    this.overlayRef!.updatePosition();

    return {
      select$: instance.select.pipe(
        tap((symbol: Extract<SymbolSelectItem, { dataSourceId: T['dataSources'][number] }>) => {
          if (this.isEmojiSelect(symbol)) {
            this.emojiRecentlyUsedService.saveEmoji(symbol.value.unified);
          }
        }),
      ),
      clear$: outputToObservable(instance.clear),
      element: location.nativeElement,
      backdropClick$: this.overlayRef!.backdropClick(),
    };
  }

  public update<T extends 'searchValue'>(
    prop: T,
    value: UnwrapInput<SymbolPickerComponent[T]>,
  ): void {
    if (!this.componentRef) return;

    this.componentRef.setInput(prop, value);
    this.componentRef.changeDetectorRef.detectChanges();
  }

  public setData(data: SymbolVirtualScrollRow): void {
    if (!this.componentRef) return;

    this.componentRef.instance.virtualScrollRows = data;
    this.componentRef.changeDetectorRef.detectChanges();
  }

  public handleKey(key: EmojiListNavigationKeys): void {
    if (!this.componentRef) return;

    this.componentRef.instance.handleKey(key);
  }

  public close(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.componentRef = null;
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.positionStrategy?.dispose();
    this.positionStrategy = null;
  }

  public updatePosition(position: OverlayOrigin): void {
    if (!this.overlayRef || !this.positionStrategy) return;

    this.positionStrategy.setOrigin({
      x: position.left,
      y: position.top,
      width: (position.right || position.left) - position.left,
      height: (position.bottom || position.top) - position.top,
    });
    this.overlayRef.updatePosition();
  }

  public isOpen(): boolean {
    return !!this.componentRef;
  }

  public dataSourceHasResults(dataSourceId: `${SymbolDataSourceId}`, query: string): boolean {
    const source = this.SYMBOL_DATA_SOURCES.find(({ id }) => id === dataSourceId)!;
    return datasourceFilterFn(source, query).groups.length > 0;
  }

  public saveUsedEmoji(emojiCode: string): void {
    this.emojiRecentlyUsedService.saveEmoji(emojiCode);
  }

  private openComponent(
    component: new (...args: any[]) => SymbolPickerComponent,
    origin: OverlayOrigin,
    preferredPositions: ConnectedPosition[],
  ) {
    this.positionStrategy = new FlexibleConnectedPositionStrategy(
      {
        x: origin.left,
        y: origin.top,
        width: (origin.right || origin.left) - origin.left,
        height: (origin.bottom || origin.top) - origin.top,
      },
      this._viewportRuler,
      this._document,
      this._platform,
      this._overlayContainer,
    );
    this.positionStrategy.withGrowAfterOpen(true);
    this.positionStrategy.withPush(false);
    this.overlayRef = this.overlay.create({
      positionStrategy: this.positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });
    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.close());
    this.positionStrategy.withPositions([...preferredPositions, ...this.positions]);
    this.portalRef = new ComponentPortal(component, this.vcRef);
    this.componentRef = this.overlayRef.attach(this.portalRef);
    return this.componentRef;
  }

  private addRecentlyUsedIfNeeded(
    emojiGroups: Array<SymbolGroup<EmojiItem>>,
  ): Array<SymbolGroup<EmojiItem>> {
    const usedEmojis = this.emojiRecentlyUsedService.getUsedEmojis();
    if (!usedEmojis.length) return emojiGroups;

    const usedEmojisGroup: SymbolGroup<EmojiItem> = {
      label: EmojiCategoryTranslations.RecentlyUsed,
      data: usedEmojis,
    };
    return [usedEmojisGroup, ...emojiGroups];
  }

  private getOpenOptions(options: EmojiPickerOpenOptions): Required<EmojiPickerOpenOptions> {
    return {
      withSearchInput: options.withSearchInput || false,
      searchValue: options.searchValue || '',
      preferredPositions: options.preferredPositions || [],
      dataSources: options.dataSources,
      hasClearButton: options.hasClearButton || false,
    };
  }

  private getDataSources(ids: Array<`${SymbolDataSourceId}`>): Array<SymbolDataSource<any>> {
    return this.SYMBOL_DATA_SOURCES.filter(({ id }) => ids.includes(<SymbolDataSourceId>id)).map(
      dataSource => {
        if (dataSource.id !== SymbolDataSourceId.Emoji) return dataSource;

        return {
          ...dataSource,
          groups: this.addRecentlyUsedIfNeeded(<Array<SymbolGroup<EmojiItem>>>dataSource.groups),
        };
      },
    );
  }

  private isEmojiSelect(item: any): item is SymbolSelectEmoji {
    return item.dataSourceId === SymbolDataSourceId.Emoji;
  }
}
